import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CalendarBlank, Printer, ArrowsLeftRight, FileText, ArrowClockwise } from "@phosphor-icons/react";
import { MoneyCtx, periodLabel } from "@/components/monthly/shared";
import PeriodPicker, { currentYm, shiftYm } from "@/components/monthly/PeriodPicker";
import SummarySection from "@/components/monthly/SummarySection";
import SalesSection from "@/components/monthly/SalesSection";
import ClientsSection from "@/components/monthly/ClientsSection";
import InventorySection from "@/components/monthly/InventorySection";
import RepairsSection from "@/components/monthly/RepairsSection";
import ExpensesSection from "@/components/monthly/ExpensesSection";
import BanksSection from "@/components/monthly/BanksSection";
import SellersSection from "@/components/monthly/SellersSection";
import CompareView from "@/components/monthly/CompareView";

const INDEX = [
  { id: "resumen", n: 1, label: "Resumen general" },
  { id: "ventas", n: 2, label: "Comportamiento de ventas" },
  { id: "clientes", n: 3, label: "Clientes" },
  { id: "retencion", n: 4, label: "Retención" },
  { id: "inventario", n: 5, label: "Inventario" },
  { id: "antiguedad", n: 6, label: "Antigüedad" },
  { id: "rotacion", n: 7, label: "Rotación" },
  { id: "reparaciones", n: 8, label: "Reparaciones" },
  { id: "gastos", n: 9, label: "Gastos y rentabilidad" },
  { id: "bancos", n: 10, label: "Bancos y pagos" },
  { id: "vendedores", n: 11, label: "Vendedores" },
];

function useActiveSection(ready) {
  const [active, setActive] = useState("resumen");
  useEffect(() => {
    if (!ready) return;
    const els = INDEX.map((s) => document.getElementById(s.id)).filter(Boolean);
    const obs = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting).sort((x, y) => x.boundingClientRect.top - y.boundingClientRect.top);
      if (vis[0]) setActive(vis[0].target.id);
    }, { rootMargin: "-20% 0px -60% 0px" });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [ready]);
  return active;
}

export default function MonthlyReport() {
  const [sp, setSp] = useSearchParams();
  const period = sp.get("period") || currentYm();
  const tab = sp.get("tab") === "compare" ? "compare" : "report";
  const cmpA = sp.get("a") || shiftYm(period, -1);
  const cmpB = sp.get("b") || period;
  const [cur, setCur] = useState(() => localStorage.getItem("mr_currency") || "MXN");
  const [periods, setPeriods] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef(null);

  const setParam = (patch) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => (v === null || v === undefined ? next.delete(k) : next.set(k, v)));
    setSp(next, { replace: true });
  };

  useEffect(() => { localStorage.setItem("mr_currency", cur); }, [cur]);
  useEffect(() => { api.get("/reports/monthly/periods").then((r) => setPeriods(r.data || [])).catch(() => {}); }, []);

  const load = () => {
    setLoading(true);
    api.get("/reports/monthly", { params: { period } })
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "No se pudo generar el reporte"))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line
  useEffect(load, [period]);

  const money = useMemo(() => ({ cur, rate: report?.exchange_rate || 1 }), [cur, report]);
  const active = useActiveSection(!!report && tab === "report");
  const goTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <MoneyCtx.Provider value={money}>
      <div className="p-6 sm:p-8 space-y-6" data-testid="monthly-report-page">
        <header className="flex items-end justify-between flex-wrap gap-4 no-print">
          <div>
            <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">FOTOGRAFÍA MENSUAL DE LA OPERACIÓN</div>
            <h1 className="font-heading text-4xl font-extrabold tracking-tight flex items-center gap-3">
              <CalendarBlank size={32} weight="duotone" className="text-[#002FA7]" /> Reporte Mensual
            </h1>
            <p className="text-zinc-500 mt-1 text-sm max-w-2xl">Ventas, clientes, inventario, reparaciones, gastos, bancos y vendedores en un solo lugar. Sólo super admin.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex border border-zinc-200 bg-white text-xs font-semibold" data-testid="mr-tabs">
              <button type="button" onClick={() => setParam({ tab: null })} className={`px-3 h-9 inline-flex items-center gap-1.5 ${tab === "report" ? "bg-[#002FA7] text-white" : "text-zinc-600 hover:bg-zinc-50"}`} data-testid="mr-tab-report"><FileText size={14} /> Reporte</button>
              <button type="button" onClick={() => setParam({ tab: "compare", a: cmpA, b: cmpB })} className={`px-3 h-9 inline-flex items-center gap-1.5 ${tab === "compare" ? "bg-[#002FA7] text-white" : "text-zinc-600 hover:bg-zinc-50"}`} data-testid="mr-tab-compare"><ArrowsLeftRight size={14} /> Comparar meses</button>
            </div>
            <div className="inline-flex border border-zinc-200 bg-white text-xs font-semibold" data-testid="mr-currency-switch">
              {["MXN", "USD"].map((c) => (
                <button key={c} type="button" onClick={() => setCur(c)} className={`px-3 h-9 ${cur === c ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`} data-testid={`mr-currency-${c}`}>{c === "MXN" ? "MX$" : "USD"}</button>
              ))}
            </div>
            {tab === "report" && (
              <>
                <PeriodPicker value={period} onChange={(v) => setParam({ period: v })} periods={periods} />
                <button type="button" onClick={load} className="h-9 w-9 border border-zinc-200 bg-white flex items-center justify-center hover:bg-zinc-50" title="Actualizar" data-testid="mr-refresh"><ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /></button>
                <button type="button" onClick={() => window.print()} className="h-9 px-3 bg-[#002FA7] text-white text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-[#001a6a]" data-testid="mr-print"><Printer size={14} weight="bold" /> Imprimir / PDF</button>
              </>
            )}
          </div>
        </header>

        {tab === "compare" ? (
          <CompareView a={cmpA} b={cmpB} periods={periods} onChange={(a, b) => setParam({ tab: "compare", a, b })} />
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-[200px_1fr] gap-6">
            <nav className="hidden xl:block no-print" aria-label="Secciones">
              <ul className="sticky top-6 space-y-0.5 text-xs" data-testid="mr-index">
                {INDEX.map((s) => (
                  <li key={s.id}>
                    <button type="button" onClick={() => goTo(s.id)} className={`w-full text-left px-2.5 py-1.5 flex items-center gap-2 border-l-2 transition-colors ${active === s.id ? "border-[#002FA7] text-[#002FA7] font-semibold bg-[#002FA7]/5" : "border-transparent text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50"}`} data-testid={`mr-index-${s.id}`}>
                      <span className="font-mono-data text-[10px] w-4">{String(s.n).padStart(2, "0")}</span>{s.label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            <div ref={printRef} className="space-y-10 min-w-0" data-testid="mr-content">
              <div className="print-only hidden">
                <h1 className="font-heading text-3xl font-extrabold">Reporte Mensual · {periodLabel(period)}</h1>
                <p className="text-xs text-zinc-500">APC · Generado {new Date().toLocaleString("es-MX")} · Moneda {cur === "USD" ? "USD" : "MX$"}{report ? ` · TC ${report.exchange_rate}` : ""}</p>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2 border-b border-zinc-200 pb-3" data-testid="mr-period-header">
                <div>
                  <div className="font-heading text-2xl font-bold">{periodLabel(period)}</div>
                  {report && (
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {report.is_current_month ? <span className="text-amber-700 font-semibold" data-testid="mr-current-month-badge">Mes en curso · {report.elapsed_days} de {report.days_in_month} días</span> : `${report.days_in_month} días`}
                      {" · "}TC {report.exchange_rate} MXN/USD{" · "}<span title={report.rule}>Solo notas pagadas o con pago</span>
                    </div>
                  )}
                </div>
                {loading && <span className="text-xs text-zinc-500 animate-pulse" data-testid="mr-loading">Generando reporte…</span>}
              </div>

              {report && (
                <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
                  <div className="space-y-10">
                    <SummarySection r={report} />
                    <SalesSection r={report} />
                    <ClientsSection r={report} />
                    <InventorySection r={report} />
                    <RepairsSection r={report} />
                    <ExpensesSection r={report} />
                    <BanksSection r={report} />
                    <SellersSection r={report} />
                  </div>
                </div>
              )}
              {!report && !loading && <div className="text-sm text-zinc-500 py-10 text-center dotted-bg">No se pudo cargar el reporte.</div>}
            </div>
          </div>
        )}
      </div>
    </MoneyCtx.Provider>
  );
}
